import React, { useState, useMemo, useEffect, useRef } from 'react';
import { IconSearch, IconSparkles } from '@tabler/icons-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/admin/components/ui/dialog';
import { Input } from '@/admin/components/ui/input';
import { ScrollArea } from '@/admin/components/ui/scroll-area';
import { Skeleton } from '@/admin/components/ui/skeleton';
import { cn } from '@/admin/lib/utils';
import type { Skill } from '@/admin/lib/api';

interface SkillPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skills: Skill[];
  onSelect: (skill: Skill) => void;
  loading?: boolean;
}

export const SkillPickerDialog: React.FC<SkillPickerDialogProps> = ({
  open,
  onOpenChange,
  skills,
  onSelect,
  loading,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const filteredSkills = useMemo(() => {
    const skillList = skills || [];
    if (!search) return skillList;
    const lowerSearch = search.toLowerCase();
    return skillList.filter(
      skill =>
        skill.name.toLowerCase().includes(lowerSearch) ||
        skill.display_name?.toLowerCase().includes(lowerSearch) ||
        skill.description?.toLowerCase().includes(lowerSearch)
    );
  }, [skills, search]);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Reset search when dialog opens
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedIndex(0);
    }
  }, [open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < filteredSkills.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredSkills[selectedIndex]) {
            onSelect(filteredSkills[selectedIndex]);
            onOpenChange(false);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, filteredSkills, selectedIndex, onSelect, onOpenChange]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = listRef.current?.querySelector(
      '[data-selected="true"]'
    );
    selectedElement?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleSelect = (skill: Skill) => {
    onSelect(skill);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>Insert Skill Card</DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="p-3 border-b">
          <div className="relative">
            <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search skills..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-10 bg-muted/50 border-0 focus-visible:ring-1"
              autoFocus
            />
          </div>
        </div>

        {/* List */}
        <ScrollArea className="max-h-[400px]">
          <div ref={listRef} className="p-2">
            {loading ? (
              <div className="space-y-1">
                {[1, 2, 3, 4].map(i => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-2 rounded-lg"
                  >
                    <Skeleton className="w-14 h-14 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredSkills.length === 0 ? (
              <div className="py-12 text-center">
                <IconSparkles className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">No skills found</p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredSkills.map((skill, index) => (
                  <button
                    key={skill.id}
                    data-selected={index === selectedIndex}
                    onClick={() => handleSelect(skill)}
                    className={cn(
                      'w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all',
                      index === selectedIndex
                        ? 'bg-primary/10 ring-1 ring-primary/20'
                        : 'hover:bg-muted/70'
                    )}
                  >
                    {/* Thumbnail */}
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gradient-to-br from-muted to-muted/50 shrink-0 flex items-center justify-center">
                      {skill.hero_url ? (
                        <img
                          src={skill.hero_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <IconSparkles className="h-6 w-6 text-muted-foreground/40" />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 py-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">
                          {skill.display_name || skill.name}
                        </span>
                        {skill.featured && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 font-medium">
                            Featured
                          </span>
                        )}
                      </div>
                      {skill.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                          {skill.description}
                        </p>
                      )}
                      <p className="text-[11px] text-muted-foreground/60 font-mono mt-1">
                        {skill.name}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="px-3 py-2 border-t bg-muted/30 flex items-center gap-4 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">
              ↑
            </kbd>
            <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">
              ↓
            </kbd>
            <span className="ml-1">navigate</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">
              ↵
            </kbd>
            <span className="ml-1">select</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">
              esc
            </kbd>
            <span className="ml-1">close</span>
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
